<template>
  <form
    class="flex min-h-full min-w-full grow flex-col"
    @submit="onSubmit"
  >
    <div class="mb-6 flex-1 space-y-6">
      <div class="flex w-full items-center justify-center">
        <div
          class="relative flex size-12 items-center justify-center rounded border border-background-fg/10 bg-primary font-semibold text-primary-fg"
          :style="{
            backgroundColor: values.color || undefined,
          }"
        >
          {{ buildShortString(values.name ?? "") }}
        </div>
      </div>
      <TextInputFormField
        name="name"
        label="Category Name"
      />

      <BaseColorInput name="color" label="Display Color" />

      <QueryLoading v-if="isCategoriesLoading" />
      <QueryError v-else-if="isCategoriesError">
        Failed to retrieve parent categories
      </QueryError>
      <BaseSelectInput
        v-else
        :options="categoryOptions"
        label="Parent Category"
        name="parentCategoryId"
      />
    </div>

    <ActionButtonPrimary
      type="submit"
      class="mt-auto w-full"
      :disabled="isCategoriesError || isCategoriesLoading"
    >
      Save Category
    </ActionButtonPrimary>
  </form>
</template>

<script setup lang="ts">
import {
  useCategoryForm,
  type CategoryFormOutput,
} from "@/forms/categoryForm"
import { useCategories } from "@/services/categories/listCategories"
import { retrieveCategory } from "@/services/categories/retrieveCategory"

const props = defineProps<{
  categoryId?: string
}>()

const emit = defineEmits<{
  (event: "submit", params: CategoryFormOutput): void
}>()

const {
  data: categories,
  isError: isCategoriesError,
  isLoading: isCategoriesLoading,
} = useCategories()
const { startLoading, stopLoading } =
  usePageBlockingLoading()

const { handleSubmit, values, setValues } =
  useCategoryForm()

const onSubmit = handleSubmit(async (params) => {
  emit("submit", params)
})

onMounted(async () => {
  if (!props.categoryId) return

  startLoading()
  try {
    const category = await retrieveCategory(
      props.categoryId
    )

    setValues(category)
  } finally {
    stopLoading()
  }
})

const categoryOptions = computed(() => {
  const categoryId = props.categoryId

  return [
    {
      id: null,
      label: "---",
    },
    ...(categoryId
      ? categories.value.filter(
          ({ id, path }) =>
            id !== categoryId && !path.includes(categoryId)
        )
      : categories.value
    ).map(({ id, namePath }) => ({
      id: id,
      label: namePath,
    })),
  ]
})
</script>
